import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { validColorValidator } from '@relynn/ngx-colors';
import { GenericFont, getEnumKeys } from 'common';
import {
  Color,
  DocumentAnnotation,
  EllipseAnnotation,
  EndCapHeight,
  EndCapStyle,
  EndCapWidth,
  Font,
  LineAnnotation,
  PathAnnotation,
  RectangleAnnotation,
  StampAnnotation,
  TextAnnotation,
  createFont,
} from 'models';
import { ifValidator } from 'ngxtension/if-validator';
import { DocumentAnnotationService } from 'src/app/services/document-annotation.service';

/** Describes the fill properties form. */
interface FillForm {
  /** Color. */
  color: FormControl<string>;
  /** Opacity. */
  opacity: FormControl<number>;
}

/** Describes the text properties form. */
interface TextForm {
  /** Text alignment. */
  alignment: FormControl<string>;
  /** Font color. */
  color: FormControl<string>;
  /** Font name. This should be a value from the GenericFont enum. */
  fontName: FormControl<GenericFont>;
  /** Font size. */
  fontSize: FormControl<number>;
  /**
   * Font emphasis such as bold, italic, etc.
   *
   * Each value in the array represents one of the font emphasis options.
   */
  emphasis: FormControl<string[]>;
  /** Text opacity. */
  opacity: FormControl<number>;
  /** Text value that will be displayed in the annotation such as in a note or a stamp. */
  value: FormControl<string>;
}

/** Describes the outline properties form. */
interface OutlineForm {
  /** Color. */
  color: FormControl<string>;
  /** Opacity.  */
  opacity: FormControl<number>;
  /** Border thickness. */
  thickness: FormControl<number>;
  /** Border corner radius. */
  cornerRadius: FormControl<number>;
}

/** Describes a cap properties form. */
interface CapForm {
  /** Color. */
  width: FormControl<EndCapWidth>;
  /** Opacity.  */
  height: FormControl<EndCapHeight>;
  /** Border thickness. */
  style: FormControl<EndCapStyle>;
}

/** Describes the annotation properties form. */
interface AnnotationPropertiesForm {
  /** Should the annotation be burned to the document. */
  burn: FormControl<boolean>;
  /** Fill properties. */
  fill: FormGroup<FillForm>;
  /** Outline properties. */
  outline: FormGroup<OutlineForm>;
  /** Text properties. */
  text: FormGroup<TextForm>;
  /** Start cap properties. */
  startCap: FormGroup<CapForm>;
  /** End cap properties. */
  endCap: FormGroup<CapForm>;
}

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'annotation-properties',
  templateUrl: './annotation-properties.component.html',
  styleUrl: './annotation-properties.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AnnotationPropertiesComponent {
  @ViewChild('textArea') textArea: ElementRef;
  /** Annotation properties form. */
  annotationPropertiesForm: FormGroup<AnnotationPropertiesForm>;
  /** An array of available fonts to be rendered in the mat-select. */
  availableFonts = Object.values(GenericFont);
  /** Does the user have modify document permissions. */
  modifyDocumentPermission: boolean = false;
  /** Should fill options be displayed. */
  showFill: boolean;
  /** Should outline options be displayed. */
  showOutline: boolean;
  /** Display end cap options. */
  showEndCap: boolean;
  /** Display start cap options. */
  showStartCap: boolean;
  /** Should text options be displayed. */
  showText: boolean;
  /** Should corner radius be displayed. */
  showCornerRadius: boolean = false;
  /** Is the annotation a stamp. */
  isStamp: boolean = false;
  /** Using EndCapWidth enum for drop down. */
  capWidth = EndCapWidth;
  /** List of EndCapWidth enum values. */
  capWidthKeys = getEnumKeys(EndCapWidth);
  /** Using EndCapHeight enum for drop down. */
  capHeight = EndCapHeight;
  /** List of EndCapHeight enum values. */
  capHeightKeys = getEnumKeys(EndCapHeight);
  /** Using EndCapStyle enum for drop down. */
  capStyle = EndCapStyle;
  /** List of EndCapStyle enum values. */
  capStyleKeys = getEnumKeys(EndCapStyle);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DocumentAnnotation,
    private dialog: MatDialogRef<AnnotationPropertiesComponent>,
    private formBuilder: FormBuilder,
    private translate: TranslocoService,
    private annotationService: DocumentAnnotationService
  ) {
    this.annotationPropertiesForm =
      this.formBuilder.group<AnnotationPropertiesForm>({
        burn: new FormControl<boolean>(false, { nonNullable: true }),
        fill: this.formBuilder.group<FillForm>({
          color: new FormControl('', {
            nonNullable: true,
            validators: ifValidator(
              () => this.showFill,
              [Validators.required, validColorValidator()]
            ),
          }),
          opacity: new FormControl(1, { nonNullable: true }),
        }),
        outline: this.formBuilder.group<OutlineForm>({
          color: new FormControl('', {
            nonNullable: true,
            validators: ifValidator(
              () => this.showOutline,
              [Validators.required, validColorValidator()]
            ),
          }),
          opacity: new FormControl(1, { nonNullable: true }),
          thickness: new FormControl(1, { nonNullable: true }),
          cornerRadius: new FormControl(0, { nonNullable: true }),
        }),
        text: this.formBuilder.group<TextForm>({
          alignment: new FormControl('', { nonNullable: true }),
          color: new FormControl('', {
            nonNullable: true,
            validators: ifValidator(
              () => this.showText,
              [Validators.required, validColorValidator()]
            ),
          }),
          fontName: new FormControl(GenericFont.SansSerif, {
            nonNullable: true,
          }),
          fontSize: new FormControl(70, {
            nonNullable: true,
            validators: ifValidator(
              () => this.showText && !this.isStamp,
              [Validators.required]
            ),
          }),
          emphasis: new FormControl([], { nonNullable: true }),
          opacity: new FormControl(1, { nonNullable: true }),
          value: new FormControl('', { nonNullable: true }),
        }),
        startCap: this.formBuilder.group<CapForm>({
          width: new FormControl(EndCapWidth.Medium, { nonNullable: true }),
          height: new FormControl(EndCapHeight.Medium, { nonNullable: true }),
          style: new FormControl(EndCapStyle.None, { nonNullable: true }),
        }),
        endCap: this.formBuilder.group<CapForm>({
          width: new FormControl(EndCapWidth.Medium, { nonNullable: true }),
          height: new FormControl(EndCapHeight.Medium, { nonNullable: true }),
          style: new FormControl(EndCapStyle.FilledArrow, {
            nonNullable: true,
          }),
        }),
      });

    this.modifyDocumentPermission =
      this.annotationService.modifyDocumentPermissions;

    // Populate dialog data based on annotation type.
    if (data instanceof RectangleAnnotation) {
      this.setShowProperties(true, !data.isHighlight, false, false, false);
      this.setFill(data.color.hexCode, data.color.opacity);
      this.setOutline(
        data.border.color.hexCode,
        data.border.color.opacity,
        data.border.thickness
      );
    } else if (data instanceof StampAnnotation) {
      this.setShowProperties(false, true, true, false, false);
      this.showCornerRadius = true;
      this.isStamp = true;
      this.setOutline(
        data.border.color.hexCode,
        data.border.color.opacity,
        data.border.thickness,
        data.cornerRadius
      );
      this.setText(
        createFont({ name: data.font, size: data.fontSize }),
        data.color.hexCode,
        data.text
      );
    } else if (data instanceof TextAnnotation) {
      this.setShowProperties(true, true, true, false, false);
      this.setFill(data.fillColor.hexCode, data.fillColor.opacity);
      this.setOutline(
        data.border.color.hexCode,
        data.border.color.opacity,
        data.border.thickness,
        1
      );
      this.setText(data.font, data.color.hexCode, data.text, data.alignment);
    } else if (data instanceof PathAnnotation) {
      this.setShowProperties(false, true, false, false, false);
      this.setOutline(data.color.hexCode, data.color.opacity, data.thickness);
    } else if (data instanceof LineAnnotation) {
      this.setShowProperties(false, true, false, true, true);
      this.setOutline(
        data.line.color.hexCode,
        data.line.color.opacity,
        data.line.thickness
      );
      this.setEndCap(
        data.endcap.size.height,
        data.endcap.style,
        data.endcap.size.width
      );
      this.setStartCap(
        data.startcap.size.height,
        data.startcap.style,
        data.startcap.size.width
      );
    } else if (data instanceof EllipseAnnotation) {
      this.setShowProperties(true, true, false, false, false);
      this.setFill(data.color.hexCode, data.color.opacity);
      this.setOutline(
        data.border.color.hexCode,
        data.border.color.opacity,
        data.border.thickness
      );
    }

    this.annotationPropertiesForm.controls.burn.setValue(data.burn);

    // We need to listen for changes to color controls to ensure the color picker and inputs are synced
    this.annotationPropertiesForm.controls.fill.controls.color.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((value) => {
        this.annotationPropertiesForm.controls.fill.controls.color.setValue(
          value,
          { onlySelf: true, emitEvent: false, emitModelToViewChange: true }
        );
      });
    this.annotationPropertiesForm.controls.outline.controls.color.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((value) => {
        this.annotationPropertiesForm.controls.outline.controls.color.setValue(
          value,
          { onlySelf: true, emitEvent: false, emitModelToViewChange: true }
        );
      });
    this.annotationPropertiesForm.controls.text.controls.color.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((value) => {
        this.annotationPropertiesForm.controls.text.controls.color.setValue(
          value,
          { onlySelf: true, emitEvent: false, emitModelToViewChange: true }
        );
      });

    dialog.keydownEvents().subscribe((event: KeyboardEvent) => {
      const focusedElement = document.activeElement;
      if (event.key === 'Enter' && focusedElement?.tagName !== 'TEXTAREA') {
        event.preventDefault();
        this.save();
      }
    });

    dialog
      .afterOpened()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        setTimeout(() => {
          if (this.textArea) {
            this.textArea.nativeElement.focus();
          }
        });
      });
  }
  getValidationErrorMessage(control: FormControl): string {
    if (control.hasError('required')) {
      return this.translate.translate('REQUIRED');
    } else if (control.hasError('invalidColor')) {
      return this.translate.translate('COLOR_NOT_VALID');
    }

    return '';
  }

  /**
   * Closes and save the data in the dialog.
   */
  save() {
    let anno = this.data;
    const { burn, fill, outline, text, endCap, startCap } =
      this.annotationPropertiesForm.getRawValue();
    const textBold = text.emphasis.includes('bold');
    const textItalic = text.emphasis.includes('italic');
    const textStrikeout = text.emphasis.includes('strikeout');
    const textUnderline = text.emphasis.includes('underline');
    if (anno instanceof RectangleAnnotation) {
      anno.color = Color.convertFromHex(fill.color, fill.opacity);
      anno.border.color = Color.convertFromHex(outline.color, outline.opacity);
      anno.border.thickness = outline.thickness;
    } else if (anno instanceof StampAnnotation) {
      // Probably need to update the data so it include the thickness and corner radius. Seens to be missing from the object.
      anno.color = Color.convertFromHex(outline.color, outline.opacity);
      anno.font = text.fontName as GenericFont;
      anno.border.color = Color.convertFromHex(outline.color, outline.opacity);
      anno.text = text.value;
      anno.border.thickness = outline.thickness;
      anno.cornerRadius = outline.cornerRadius;
    } else if (anno instanceof TextAnnotation) {
      anno.fillColor = Color.convertFromHex(fill.color, fill.opacity);
      anno.border.color = Color.convertFromHex(outline.color, outline.opacity);
      anno.border.thickness = outline.thickness;
      anno.font = {
        bold: textBold,
        italic: textItalic,
        name: text.fontName as GenericFont,
        size: text.fontSize,
        strikeout: textStrikeout,
        underline: textUnderline,
      };
      anno.color = Color.convertFromHex(text.color, 1);
      anno.alignment = text.alignment;
      anno.text = text.value;
    } else if (anno instanceof PathAnnotation) {
      anno.color = Color.convertFromHex(outline.color, outline.opacity);
      anno.thickness = outline.thickness;
    } else if (anno instanceof LineAnnotation) {
      anno.endcap.style = endCap.style;
      anno.endcap.size = {
        height: endCap.height,
        width: endCap.width,
      };
      anno.startcap.style = startCap.style;
      anno.startcap.size = {
        height: startCap.height,
        width: startCap.width,
      };
      anno.line.color = Color.convertFromHex(outline.color, outline.opacity);
      anno.line.thickness = outline.thickness;
    } else if (anno instanceof EllipseAnnotation) {
      anno.color = Color.convertFromHex(fill.color, fill.opacity);
      anno.border.color = Color.convertFromHex(outline.color, outline.opacity);
      anno.border.thickness = outline.thickness;
    }
    anno.burn = burn;
    this.dialog.close(anno); // Pass the updated data back
  }

  toggleBurn() {
    const burn = this.annotationPropertiesForm.controls.burn.value;
    this.annotationPropertiesForm.controls.burn.setValue(!burn);
  }

  /**
   * Close dialog.
   */
  close() {
    this.dialog.close();
  }

  /**
   * Sets which properties to show in the property dialog.
   *
   * @param showFill - Shows the fill inputs.
   * @param showOutline - Shows the outline inputs.
   * @param showText - Shows the text inputs.
   */
  setShowProperties(
    showFill: boolean,
    showOutline: boolean,
    showText: boolean,
    showEndCap: boolean,
    showStartCap: boolean
  ) {
    this.showFill = showFill;
    this.showOutline = showOutline;
    this.showText = showText;
    this.showEndCap = showEndCap;
    this.showStartCap = showStartCap;
  }

  /**
   * Set fill properties.
   *
   * @param color - Fill Color.
   * @param opacity - FIll Opacity.
   */
  private setFill(color: string, opacity: number) {
    this.annotationPropertiesForm.controls.fill.controls.color.setValue(color);
    this.annotationPropertiesForm.controls.fill.controls.opacity.setValue(
      opacity
    );
  }

  /**
   * Set outline properties.
   *
   * @param color - Outline color.
   * @param opacity - Outline Opacity.
   * @param thickness - Outline thickness.
   * @param cornerRadius - Outline Corner Radius.
   */
  private setOutline(
    color: string,
    opacity: number,
    thickness: number,
    cornerRadius: number = 0
  ) {
    this.annotationPropertiesForm.controls.outline.controls.color.setValue(
      color
    );
    this.annotationPropertiesForm.controls.outline.controls.opacity.setValue(
      opacity
    );
    this.annotationPropertiesForm.controls.outline.controls.thickness.setValue(
      thickness
    );
    this.annotationPropertiesForm.controls.outline.controls.cornerRadius.setValue(
      cornerRadius
    );
  }

  /**
   * Set end cap properties.
   * @param height - Height.
   * @param style - Style.
   * @param width - Width.
   */
  private setEndCap(
    height: EndCapHeight,
    style: EndCapStyle,
    width: EndCapWidth
  ) {
    this.annotationPropertiesForm.controls.endCap.controls.height.setValue(
      height
    );
    this.annotationPropertiesForm.controls.endCap.controls.style.setValue(
      style
    );
    this.annotationPropertiesForm.controls.endCap.controls.width.setValue(
      width
    );
  }

  /**
   * Set start cap properties.
   * @param height - Height.
   * @param style - Style.
   * @param width - Width.
   */
  private setStartCap(
    height: EndCapHeight,
    style: EndCapStyle,
    width: EndCapWidth
  ) {
    this.annotationPropertiesForm.controls.startCap.controls.height.setValue(
      height
    );
    this.annotationPropertiesForm.controls.startCap.controls.style.setValue(
      style
    );
    this.annotationPropertiesForm.controls.startCap.controls.width.setValue(
      width
    );
  }

  /**
   * Set text properties.
   *
   * @param font - Font
   * @param fontSize - Font Size
   * @param color - Font Color
   */
  private setText(
    font: Font,
    color: string,
    text: string = '',
    alignment: string = 'left'
  ) {
    this.annotationPropertiesForm.controls.text.controls.alignment.setValue(
      alignment
    );
    this.annotationPropertiesForm.controls.text.controls.value.setValue(text);
    this.annotationPropertiesForm.controls.text.controls.fontName.setValue(
      font.name
    );
    this.annotationPropertiesForm.controls.text.controls.fontSize.setValue(
      font.size
    );
    this.annotationPropertiesForm.controls.text.controls.color.setValue(color);

    // Put font emphasis settings from the annotation into an array that can be used by the form control
    // and it's button toggle group.
    const emphasis: string[] = [];

    if (font.bold) {
      emphasis.push('bold');
    }

    if (font.italic) {
      emphasis.push('italic');
    }

    if (font.strikeout) {
      emphasis.push('strikeout');
    }

    if (font.underline) {
      emphasis.push('underline');
    }

    this.annotationPropertiesForm.controls.text.controls.emphasis.setValue(
      emphasis
    );
  }
}
